import { Currency } from '@seapoint/server/enums';

const MAX_FRACTION_DIGITS = 8;

// Forces the developer to use the supported NumberFormat config values to avoid runtime errors
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#never
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#maximumsignificantdigits
type BaseNumberFormatConfig =
  | Record<
      Extract<keyof Intl.NumberFormatOptions, 'signDisplay'>,
      Extract<Intl.NumberFormatOptions['signDisplay'], 'never'>
    >
  | Record<
      Extract<keyof Intl.NumberFormatOptions, 'maximumFractionDigits'>,
      Intl.NumberFormatOptions['maximumFractionDigits']
    >
  | Record<
      Extract<keyof Intl.NumberFormatOptions, 'currencyDisplay'>,
      Extract<Intl.NumberFormatOptions['currencyDisplay'], 'narrowSymbol' | 'symbol'>
    >;

const getBaseNumberFormatConfig = (currency?: Currency): BaseNumberFormatConfig => {
  return {
    signDisplay: 'never',
    maximumFractionDigits: MAX_FRACTION_DIGITS,
    // We don't want to display US$ for USD, so we use the narrowSymbol option
    // For other currencies, like AUD, we use the default ('symbol)
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#narrowsymbol
    currencyDisplay: currency === Currency.USD ? 'narrowSymbol' : 'symbol',
  };
};

type GetAmountWithCurrencyArgs = {
  amount?: number;
  currency?: Currency;
  isAccountBalance?: boolean;
};

export const getAmountWithCurrency = ({ amount, currency, isAccountBalance = false }: GetAmountWithCurrencyArgs) => {
  if (amount === undefined) {
    // em dash, take this into account for testing
    return '—';
  }

  // Amount 0 in places other than account balance should be displayed as '—'
  if (amount === 0 && !isAccountBalance) {
    return '—';
  }

  const absoluteAmount = Math.abs(amount);

  if (!currency) {
    const formattedAmount = new Intl.NumberFormat('en-GB', getBaseNumberFormatConfig(currency)).format(absoluteAmount);

    return getAmountWithSign({ amount, formattedAmount, isAccountBalance });
  }

  // TODO: I am not sure how we should handle multi-currency accounts.
  // Returning '—' for now.
  if (currency === Currency.MULTI_CURRENCY) {
    return '—';
  }

  const formattedAmount = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    ...getBaseNumberFormatConfig(currency),
  }).format(absoluteAmount);

  return getAmountWithSign({ amount, formattedAmount, isAccountBalance });
};

type GetAmountWithSignArgs = {
  amount: number;
  formattedAmount: string;
  isAccountBalance: boolean;
};

const getAmountWithSign = ({ amount, formattedAmount, isAccountBalance }: GetAmountWithSignArgs): string => {
  if (amount === 0) {
    return formattedAmount;
  }

  if (amount > 0) {
    // As per specs, positive amounts in transactions have a '+' sign
    // https://linear.app/seapoint/issue/TENG-519/font-colours-for-inbound-and-outbound-payments
    return isAccountBalance ? formattedAmount : `+${formattedAmount}`;
  }

  // As per specs, negative amounts don't have a sign
  return isAccountBalance ? `-${formattedAmount}` : formattedAmount;
};
